import * as qs from 'qs';
import type { ReportCategory, ReportURLParams } from '../../shared/types';
import type { DependencyMetadataFilterOption, FormatName, IncludeFilterOption } from '../shared/types';
import { reportTypeDefaultFormatMap, reportTypeFormatMap } from '../shared/constants';
import { nuc } from '../../../../../utils';
import { buildIncludeParams } from '../shared/utils';
import { ReportCategories } from '../../ReportGeneration/utils';
import type { FixPlansParams } from '../../../../../../shared/types/routes/fixPlans';

type Query = {
  preview?: boolean;
  download?: boolean;
  emails?: string;
};

type AttributionFormat = 'preview' | 'download' | 'security' | 'public' | 'email';

const getEndpoint = ({
  projectRevisionLocator,
  reportType,
  query,
  format,
  includeOptions,
  dependencyMetadataOptions,
  attributionFormat,
}: {
  projectRevisionLocator: string;
  query: Query;
  format: FormatName | null;
  reportType: ReportCategory;
  includeOptions: IncludeFilterOption[];
  dependencyMetadataOptions: DependencyMetadataFilterOption[];
  attributionFormat: AttributionFormat;
}) => {
  const defaultFileFormat = reportTypeDefaultFormatMap[reportType];
  const formatQuery = format ? reportTypeFormatMap[format] : defaultFileFormat;
  const urlParams: ReportURLParams = {
    ...buildIncludeParams(includeOptions),
    dependencyInfoOptions: dependencyMetadataOptions,
    format: formatQuery,
  };
  const queryString = qs.stringify({ ...query, ...urlParams });

  return `/api/revisions/${nuc(projectRevisionLocator)}/attribution/${attributionFormat}?${queryString}`;
};

const getFixPlanEndpoint = ({
  projectRevisionLocator,
  format,
  query,
}: {
  projectRevisionLocator: string;
  format: FormatName | null;
  query: FixPlansParams;
}) => {
  const queryString = qs.stringify({
    ...query,
    format: format ? reportTypeFormatMap[format] : reportTypeDefaultFormatMap[ReportCategories.FixPlan],
  });

  return `/api/revisions/${nuc(projectRevisionLocator)}/fix-plans?${queryString}`;
};

/**
 * Get the preview URL for a report at the project level
 */
export const getPreviewURL = ({
  projectRevisionLocator,
  reportType,
  format,
  includeOptions,
  dependencyMetadataOptions,
}: {
  projectRevisionLocator: string;
  reportType: ReportCategory;
  format: FormatName | null;
  includeOptions: IncludeFilterOption[];
  dependencyMetadataOptions: DependencyMetadataFilterOption[];
}) => {
  const isVulnerability = reportType === ReportCategories.Vulnerability;
  const isFixPlan = reportType === ReportCategories.FixPlan;

  if (isFixPlan) {
    return getFixPlanEndpoint({
      projectRevisionLocator,
      format,
      query: { preview: true },
    });
  }

  return getEndpoint({
    projectRevisionLocator,
    format,
    query: {
      preview: true,
    },
    reportType,
    includeOptions,
    dependencyMetadataOptions,
    attributionFormat: isVulnerability ? 'security' : 'preview',
  });
};

/**
 * Get the download URL for a report at the project level
 */
export const getDownloadURL = ({
  projectRevisionLocator,
  reportType,
  format,
  includeOptions,
  dependencyMetadataOptions,
}: {
  projectRevisionLocator: string;
  reportType: ReportCategory;
  format: FormatName | null;
  includeOptions: IncludeFilterOption[];
  dependencyMetadataOptions: DependencyMetadataFilterOption[];
}) => {
  const isVulnerability = reportType === ReportCategories.Vulnerability;
  const isFixPlan = reportType === ReportCategories.FixPlan;

  if (isFixPlan) {
    return getFixPlanEndpoint({
      projectRevisionLocator,
      format,
      query: { bundle: true },
    });
  }

  return getEndpoint({
    projectRevisionLocator,
    format,
    query: {
      download: true,
    },
    reportType,
    includeOptions,
    dependencyMetadataOptions,
    attributionFormat: isVulnerability ? 'security' : 'download',
  });
};

/**
 * Get the email URL for a report at the project level
 */
export const getEmailURL = ({
  projectRevisionLocator,
  reportType,
  format,
  includeOptions,
  dependencyMetadataOptions,
  emails,
}: {
  projectRevisionLocator: string;
  reportType: ReportCategory;
  format: FormatName | null;
  includeOptions: IncludeFilterOption[];
  dependencyMetadataOptions: DependencyMetadataFilterOption[];
  emails: string;
}) => {
  return getEndpoint({
    projectRevisionLocator,
    format,
    query: {
      emails,
    },
    reportType,
    includeOptions,
    dependencyMetadataOptions,
    attributionFormat: 'email',
  });
};

/**
 * Get the link to generate a public report URL
 */
export const getGenerateReportURL = ({
  projectRevisionLocator,
  reportType,
  format,
  includeOptions,
  dependencyMetadataOptions,
}: {
  projectRevisionLocator: string;
  reportType: ReportCategory;
  format: FormatName | null;
  includeOptions: IncludeFilterOption[];
  dependencyMetadataOptions: DependencyMetadataFilterOption[];
}) => {
  return getEndpoint({
    projectRevisionLocator,
    format,
    query: {},
    reportType,
    includeOptions,
    dependencyMetadataOptions,
    attributionFormat: 'public',
  });
};
